export type TagClass =
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "neutral"
	| "terra"
	| "ochre";

export interface StatusLabel {
	l: string;
	cls: TagClass;
}

const map = <T extends string>(d: Record<T, StatusLabel>) => {
	return (s: string): StatusLabel => d[s as T] ?? { l: s, cls: "neutral" };
};

export const statusOrder = map({
	paid: { l: "Payée", cls: "info" },
	pending: { l: "En attente", cls: "warning" },
	shipped: { l: "Expédiée", cls: "info" },
	delivered: { l: "Livrée", cls: "success" },
	cancelled: { l: "Annulée", cls: "neutral" },
	returned: { l: "Retour", cls: "warning" },
	failed: { l: "Échec", cls: "danger" },
});

/** Mapping ORD-01..08 — 13 statuts CDC §8.1.1. */
export const statusOrderV2 = map({
	DRAFT: { l: "Brouillon", cls: "neutral" },
	PENDING_PAYMENT: { l: "À payer", cls: "warning" },
	PAID: { l: "Payée", cls: "info" },
	CONFIRMED: { l: "Confirmée", cls: "info" },
	PROCURING: { l: "Approvisionnement", cls: "ochre" },
	IN_HUB: { l: "Au hub", cls: "terra" },
	READY_TO_SHIP: { l: "Prête", cls: "info" },
	SHIPPING: { l: "En livraison", cls: "info" },
	DELIVERED: { l: "Livrée", cls: "success" },
	COMPLETED: { l: "Clôturée", cls: "success" },
	CANCELLED: { l: "Annulée", cls: "neutral" },
	REFUNDED: { l: "Remboursée", cls: "warning" },
	DISPUTED: { l: "Litige", cls: "danger" },
});

export const statusSupplier = map({
	active: { l: "Actif", cls: "success" },
	warning: { l: "À surveiller", cls: "warning" },
	low: { l: "Caution basse", cls: "danger" },
	suspended: { l: "Suspendu", cls: "neutral" },
});

export const statusProduct = map({
	published: { l: "Publié", cls: "success" },
	draft: { l: "Brouillon", cls: "neutral" },
	low: { l: "Stock bas", cls: "warning" },
	out: { l: "Épuisé", cls: "danger" },
});

export const statusPayment = map({
	success: { l: "Réussie", cls: "success" },
	failed: { l: "Échec", cls: "danger" },
	pending: { l: "En attente", cls: "warning" },
	refunded: { l: "Remboursée", cls: "info" },
	// P02 — statuts longs
	INITIATED: { l: "Initiée", cls: "neutral" },
	PENDING: { l: "En attente", cls: "warning" },
	AUTHORIZED: { l: "Autorisée", cls: "info" },
	CAPTURED: { l: "Capturée", cls: "info" },
	SUCCESS: { l: "Réussie", cls: "success" },
	FAILED: { l: "Échec", cls: "danger" },
	EXPIRED: { l: "Expirée", cls: "neutral" },
	CANCELLED: { l: "Annulée", cls: "neutral" },
	REFUNDED: { l: "Remboursée", cls: "warning" },
	PARTIALLY_REFUNDED: { l: "Rbt partiel", cls: "warning" },
	DISPUTED: { l: "Litige", cls: "danger" },
	CHARGEBACK: { l: "Rétrocession", cls: "danger" },
});

export const statusRefund = map({
	PENDING_APPROVAL: { l: "À approuver", cls: "warning" },
	APPROVED: { l: "Approuvé", cls: "info" },
	EXECUTING: { l: "En cours", cls: "info" },
	SUCCESS: { l: "Effectué", cls: "success" },
	FAILED: { l: "Échec", cls: "danger" },
	CANCELLED: { l: "Annulé", cls: "neutral" },
});

export const statusDispute = map({
	OPEN: { l: "Ouvert", cls: "warning" },
	NEED_EVIDENCE: { l: "Preuves attendues", cls: "warning" },
	UNDER_REVIEW: { l: "En revue", cls: "info" },
	RESOLVED_MERCHANT_WIN: { l: "Gagné", cls: "success" },
	RESOLVED_CUSTOMER_WIN: { l: "Perdu", cls: "danger" },
	RESOLVED_PARTIAL: { l: "Partiel", cls: "warning" },
	CLOSED: { l: "Clôturé", cls: "neutral" },
});

export const statusTour = map({
	"on-time": { l: "À l'heure", cls: "success" },
	delayed: { l: "En retard", cls: "warning" },
	done: { l: "Terminée", cls: "neutral" },
	queued: { l: "À démarrer", cls: "info" },
});

export const statusTicket = map({
	open: { l: "Ouvert", cls: "warning" },
	pending: { l: "En attente client", cls: "info" },
	resolved: { l: "Résolu", cls: "success" },
	escalated: { l: "Escaladé", cls: "danger" },
});

export const statusPromo = map({
	active: { l: "Actif", cls: "success" },
	scheduled: { l: "Planifié", cls: "info" },
	depleted: { l: "Épuisé", cls: "neutral" },
	paused: { l: "Pausé", cls: "warning" },
});

export const statusSettle = map({
	ready: { l: "Prêt à payer", cls: "success" },
	pending: { l: "Validation", cls: "warning" },
	"on-hold": { l: "Bloqué", cls: "danger" },
	paid: { l: "Payé", cls: "neutral" },
	review: { l: "Revue", cls: "info" },
});
